/**
 * The LIVE strip — global chrome, above the bottom nav.
 *
 * BK, 27 Jul: "run it as a ticker of activity on the platform."
 *
 * QUIET IS A VALID STATE. If the platform has no activity this renders NOTHING —
 * not a placeholder, not a filler line, not yesterday's news dressed as live.
 * The comp shows a busy strip because the comp shows a busy night; the strip's
 * job is to report the night it is actually in. Today's real test had three
 * cards and zero claims, and a ticker that invented traffic for it would have
 * hidden exactly the signal worth seeing.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { TickerRow } from "@matchday/contracts";
import { matchApi } from "../../lib/matchApi";

/** Slow enough to read a line as it passes, on a strip roughly a screen wide. */
export const TICKER_SECONDS_PER_ROW = 4;
/** Activity is not urgent — a 60s refresh keeps it live without polling cost. */
export const TICKER_POLL_MS = 60_000;

/** One line's words. The KIND supplies the verb; the server supplies the nouns. */
export function tickerLine(row: TickerRow): string {
  const who = row.gamertag;
  switch (row.kind) {
    case "match_created":
      return `${who} posted a match`;
    case "match_completed":
      return row.opponent_gamertag !== null && row.detail !== null
        ? `${who} ${row.detail} ${row.opponent_gamertag}`
        : `${who} finished a match`;
    case "profile_created":
      return `${who} joined the night`;
    case "profile_updated":
      return `${who} changed their colours`;
    case "trophy_earned":
      return row.detail !== null && row.detail !== ""
        ? `${who} lifted the ${row.detail}`
        : `${who} won a trophy`;
    case "reward":
      return `${who} banked a reward`;
    case "chat":
      // Night room only, and never the words. The line reports that the room is
      // alive, which is the whole reason a public room appears here at all.
      return `${who} is talking in the Night Room`;
    case "climb":
      return row.detail !== null ? `${who} climbed to ${row.detail}` : `${who} is climbing`;
  }
}

export function LiveTicker() {
  const [rows, setRows] = useState<TickerRow[]>([]);
  const failedRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const res = await matchApi.getTicker();
      setRows(res.rows);
      failedRef.current = false;
    } catch {
      // A failed poll keeps the last good strip rather than blanking it. The
      // ticker is decoration; it must never be the thing that reports an outage.
      failedRef.current = true;
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = setInterval(() => void refresh(), TICKER_POLL_MS);
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [refresh]);

  if (rows.length === 0) return null;

  // Duplicated once so the marquee can loop seamlessly: the second copy is
  // scrolling into place as the first leaves. aria-hidden on the clone — a
  // screen reader should hear the feed once, not twice.
  const line = rows.map(tickerLine).join("   ·   ");

  return (
    <div className="lt-strip" data-testid="live-ticker">
      <span className="lt-badge">LIVE</span>
      <div className="lt-window">
        <div
          className="lt-track"
          style={{ animationDuration: `${rows.length * TICKER_SECONDS_PER_ROW}s` }}
        >
          <span className="lt-run">{line}</span>
          <span className="lt-run" aria-hidden="true">
            {line}
          </span>
        </div>
      </div>
    </div>
  );
}
